import React, { useState, useEffect, useRef, Suspense } from 'react';
import SEO from '../SEO';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowRight, Check, ChevronDown, ChevronUp,
  Globe, Shield, BarChart3, Zap, Target, Cpu,
  Activity, Moon, Radio, MapPin, Link2,
  TrendingUp, Search, Users, Sparkles, Flame, Trophy,
  Calendar, MousePointer, Quote, BadgeCheck, Play,
  Clock, Eye, Server, Lock, Mail, Phone, Loader2, AlertCircle
} from 'lucide-react';
import AuthModal from './AuthModal';
import LazySection from './LazySection';
import LiveCounter from './LiveCounter';
import { trackEvent } from '../../services/analytics';
import { db, type FreeTrafficWebsiteCheckResponse } from '../../services/db';
import { COUNTRIES_LIST } from '../../constants';

const LazyProblem = React.lazy(() => import('./LandingLazyContent').then(m => ({ default: m.ProblemSection })));
const LazyHowItWorks = React.lazy(() => import('./LandingLazyContent').then(m => ({ default: m.HowItWorksSection })));
const LazyFeatures = React.lazy(() => import('./LandingLazyContent').then(m => ({ default: m.FeaturesSection })));
const LazyPricing = React.lazy(() => import('./LandingLazyContent').then(m => ({ default: m.PricingSection })));
const LazyTestimonials = React.lazy(() => import('./LandingLazyContent').then(m => ({ default: m.TestimonialsSection })));
const LazyComparison = React.lazy(() => import('./LandingLazyContent').then(m => ({ default: m.ComparisonSection })));
const LazyGuarantee = React.lazy(() => import('./LandingLazyContent').then(m => ({ default: m.GuaranteeSection })));
const LazyFaq = React.lazy(() => import('./LandingLazyContent').then(m => ({ default: m.FaqSection })));
import { TIERS, PRICING_MATRIX, VOLUME_STEPS, formatPrice, formatCPM, getCPM, TierId } from '../../constants/pricing';

const TRAFFIC_LOCATION_OPTIONS = [
  { code: 'ANY', name: 'Any location' },
  { code: 'WORLD', name: 'Worldwide mix' },
  ...COUNTRIES_LIST.filter(({ code }) => code !== 'AQ').sort((a, b) => a.name.localeCompare(b.name)),
];

const FREE_TRAFFIC_DASHBOARD_HANDOFF_KEY = 'tc_free_traffic_dashboard_handoff';

/* Chart bar grow animation + hero entrance/live-feed animations */
const chartAnimationCSS = `
@keyframes growBar { from { transform: scaleY(0); transform-origin: bottom; } to { transform: scaleY(1); transform-origin: bottom; } }
@keyframes tcFadeUp { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
@keyframes tcRowIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
@keyframes tcFloat { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-7px); } }
@keyframes tcCtaGridDrift { from { background-position: 0 0, 0 0; } to { background-position: 48px 48px, -48px 48px; } }
@keyframes tcCtaBeamSweep { 0% { transform: translateX(-65%) skewX(-18deg); opacity: 0; } 18%, 70% { opacity: 1; } 100% { transform: translateX(165%) skewX(-18deg); opacity: 0; } }
@keyframes tcCtaLinePulse { 0%, 100% { transform: translateY(0); opacity: 0.28; } 50% { transform: translateY(-10px); opacity: 0.62; } }
.tc-fade-up { opacity: 0; animation: tcFadeUp 0.7s cubic-bezier(0.16, 1, 0.3, 1) both; }
.tc-row-in { animation: tcRowIn 0.45s ease-out both; }
.tc-float { animation: tcFloat 6s ease-in-out infinite; }
.tc-cta-grid { animation: tcCtaGridDrift 18s linear infinite; }
.tc-cta-beam { animation: tcCtaBeamSweep 8s ease-in-out infinite; }
.tc-cta-line { animation: tcCtaLinePulse 7s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) {
  .tc-fade-up, .tc-row-in, .tc-float, .tc-cta-grid, .tc-cta-beam, .tc-cta-line { animation: none !important; opacity: 1 !important; }
}
.tc-no-entrance .tc-fade-up { animation: none; opacity: 1; }
.tc-no-entrance .tc-chart-bar { animation: none !important; }`;

/* ─── Animated Counter Hook ────────────────────────────────────────────── */
const useCountUp = (end: number, duration = 2000, trigger = true) => {
  const [count, setCount] = useState(0);
  const ref = useRef<number | null>(null);

  useEffect(() => {
    if (!trigger) return;
    const start = 0;
    const startTime = performance.now();
    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(start + (end - start) * eased));
      if (progress < 1) ref.current = requestAnimationFrame(animate);
    };
    ref.current = requestAnimationFrame(animate);
    return () => { if (ref.current) cancelAnimationFrame(ref.current); };
  }, [end, duration, trigger]);

  return count;
};

/* ─── Animated Counter Component ───────────────────────────────────────── */
const AnimatedCounter: React.FC<{ end: number; duration?: number; trigger?: boolean; suffix?: string }> = ({ end, duration = 2000, trigger = true, suffix = '' }) => {
  const count = useCountUp(end, duration, trigger);
  return <>{count.toLocaleString()}{suffix}</>;
};

/* ─── Live Ticker Hook — counts up to base, then random-walks upward ───── */
const useLiveTicker = (base: number, rampMs = 1800, tickMs = 3200, maxStep = 4) => {
  const value = useCountUp(base, rampMs);
  const [extra, setExtra] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setExtra(e => e + 1 + Math.floor(Math.random() * maxStep));
    }, tickMs + Math.floor(Math.random() * 1500));
    return () => clearInterval(id);
  }, [tickMs, maxStep]);
  return value + extra;
};

/* ─── Live Country Feed — cycles visitor rows like a real-time stream ──── */
const FEED_POOL = [
  { country: '🇺🇸 United States', pages: '4 pages', time: '2m 45s' },
  { country: '🇩🇪 Germany', pages: '3 pages', time: '1m 32s' },
  { country: '🇬🇧 United Kingdom', pages: '6 pages', time: '4m 10s' },
  { country: '🇫🇷 France', pages: '2 pages', time: '1m 08s' },
  { country: '🇧🇷 Brazil', pages: '5 pages', time: '3m 21s' },
  { country: '🇯🇵 Japan', pages: '3 pages', time: '2m 02s' },
  { country: '🇨🇦 Canada', pages: '4 pages', time: '2m 57s' },
  { country: '🇦🇺 Australia', pages: '2 pages', time: '1m 44s' },
];

const useLiveFeed = (size = 3, intervalMs = 3600) => {
  const [rows, setRows] = useState(() => FEED_POOL.slice(0, size).map((r, i) => ({ ...r, key: i })));
  const nextRef = useRef(size);
  useEffect(() => {
    const id = setInterval(() => {
      setRows(prev => {
        const next = FEED_POOL[nextRef.current % FEED_POOL.length];
        nextRef.current += 1;
        return [{ ...next, key: nextRef.current, live: true }, ...prev.slice(0, size - 1)];
      });
    }, intervalMs);
    return () => clearInterval(id);
  }, [size, intervalMs]);
  return rows;
};

/* ─── Intersection Observer Hook ───────────────────────────────────────── */
const useInView = (threshold = 0.2) => {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);

  return [ref, inView] as const;
};

/* ─── Component ────────────────────────────────────────────────────────── */
interface OptimizedLandingPageProps {
  onLogin: () => void;
  onNavigate: (page: string) => void;
}

type WebsiteCheckStatus = 'idle' | 'checking' | 'valid' | 'invalid' | 'error';

const LazyLandingFallback: React.FC<{ minHeight?: string }> = ({ minHeight = '400px' }) => (
  <div
    className="flex items-center justify-center bg-gray-50"
    style={{ minHeight }}
    aria-hidden="true"
  >
    <div className="h-8 w-8 animate-spin rounded-full border-t-2 border-[#ff4d00]" />
  </div>
);

const OptimizedLandingPage: React.FC<OptimizedLandingPageProps> = ({ onLogin, onNavigate }) => {
  const [scrolled, setScrolled] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('signup');
  const [authMessage, setAuthMessage] = useState('');
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [selectedTier, setSelectedTier] = useState<TierId>('professional');
  const location = useLocation();
  const claimFormRef = useRef<HTMLFormElement>(null);
  const [claimForm, setClaimForm] = useState({
    email: '',
    whatsappNumber: '',
    websiteUrl: '',
    trafficLocation: 'Any location',
    honeypot: '',
  });
  const [claimSubmitting, setClaimSubmitting] = useState(false);
  const [claimSuccess, setClaimSuccess] = useState('');
  const [claimError, setClaimError] = useState('');
  const [claimVerificationLeadId, setClaimVerificationLeadId] = useState('');
  const [claimVerificationPhone, setClaimVerificationPhone] = useState('');
  const [claimVerificationCode, setClaimVerificationCode] = useState('');
  const [claimVerificationSubmitting, setClaimVerificationSubmitting] = useState(false);
  const [claimProjectId, setClaimProjectId] = useState('');
  const [claimTemporaryPassword, setClaimTemporaryPassword] = useState('');
  const [claimDebugVerificationCode, setClaimDebugVerificationCode] = useState('');
  const [countrySearch, setCountrySearch] = useState('Any location');
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const countryDropdownRef = useRef<HTMLDivElement>(null);
  const [websiteCheckStatus, setWebsiteCheckStatus] = useState<WebsiteCheckStatus>('idle');
  const [websiteCheck, setWebsiteCheck] = useState<FreeTrafficWebsiteCheckResponse | null>(null);
  const [websiteCheckMessage, setWebsiteCheckMessage] = useState('');

  // Stats animated counters
  const [statsRef, statsInView] = useInView(0.3);
  const [ctaRef, ctaInView] = useInView(0.15);

  // Live hero elements — ticking campaign count, dashboard visitors, cycling feed
  const liveCampaigns = useLiveTicker(14847, 1800, 4200, 3);
  const liveVisitors = useLiveTicker(12405, 1600, 2800, 8);
  const liveFeed = useLiveFeed(3, 3600);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!countryDropdownRef.current?.contains(event.target as Node)) {
        setCountryDropdownOpen(false);
        setCountrySearch(claimForm.trafficLocation);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [claimForm.trafficLocation]);

  useEffect(() => {
    const websiteUrl = claimForm.websiteUrl.trim();
    if (!websiteUrl || websiteUrl.length < 4 || !websiteUrl.includes('.')) {
      setWebsiteCheckStatus('idle');
      setWebsiteCheck(null);
      setWebsiteCheckMessage('');
      return;
    }

    let cancelled = false;
    setWebsiteCheckStatus('checking');
    setWebsiteCheck(null);
    setWebsiteCheckMessage('Checking domain, previous claims, and GA/GTM tags...');

    const timeoutId = window.setTimeout(async () => {
      try {
        const result = await db.checkFreeTrafficWebsite(websiteUrl);
        if (cancelled) return;
        setWebsiteCheck(result);
        setWebsiteCheckStatus(result.eligible ? 'valid' : 'invalid');
        setWebsiteCheckMessage(
          result.eligible
            ? 'Your site passed the free traffic check. Verify WhatsApp to launch.'
            : 'Fix the failed checks before claiming free traffic.'
        );
      } catch (error) {
        if (cancelled) return;
        const message = error instanceof Error ? error.message : 'Failed to check website eligibility';
        setWebsiteCheckStatus('error');
        setWebsiteCheck(null);
        setWebsiteCheckMessage(message);
      }
    }, 650);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [claimForm.websiteUrl]);

  const handleOpenAuth = (mode: 'login' | 'signup') => {
    onNavigate(mode);
  };

  const focusFreeTrafficForm = () => {
    trackEvent('cta_click', { cta_text: 'Get My Free Traffic', cta_location: 'hero', page: location.pathname });
    claimFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => claimFormRef.current?.querySelector<HTMLInputElement>('input[name="email"]')?.focus(), 350);
  };

  const handleClaimInputChange = (field: keyof typeof claimForm, value: string) => {
    setClaimForm(prev => ({ ...prev, [field]: value }));
    if (claimError) setClaimError('');
    if (claimSuccess) setClaimSuccess('');
    if (field !== 'honeypot' && claimVerificationLeadId) {
      setClaimVerificationLeadId('');
      setClaimVerificationPhone('');
      setClaimVerificationCode('');
      setClaimProjectId('');
      setClaimTemporaryPassword('');
      setClaimDebugVerificationCode('');
    }
  };

  const filteredTrafficLocations = TRAFFIC_LOCATION_OPTIONS.filter(({ name }) =>
    name.toLowerCase().includes(countrySearch.trim().toLowerCase())
  ).slice(0, 12);

  const handleCountrySelect = (name: string) => {
    handleClaimInputChange('trafficLocation', name);
    setCountrySearch(name);
    setCountryDropdownOpen(false);
  };

  const handleFreeTrafficVerify = async () => {
    const code = claimVerificationCode.trim();
    if (!claimVerificationLeadId) return;
    if (code.length < 4) {
      setClaimError('Enter the WhatsApp verification code.');
      return;
    }

    setClaimError('');
    setClaimSuccess('');
    setClaimVerificationSubmitting(true);

    try {
      const result = await db.verifyFreeTrafficClaim(claimVerificationLeadId, code);
      setClaimProjectId(result.project_id);
      setClaimTemporaryPassword(result.temporary_password || '');
      setClaimVerificationCode('');
      setClaimSuccess(result.message || 'Verified. Your account and campaign are now active.');
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(FREE_TRAFFIC_DASHBOARD_HANDOFF_KEY, JSON.stringify({
          email: result.email,
          temporaryPassword: result.temporary_password || '',
          projectId: result.project_id,
          message: result.message || 'Verified. Your account and campaign are now active.',
        }));
      }
      trackEvent('free_traffic_claim_verified', {
        page: location.pathname,
        project_id: result.project_id,
        status: result.status,
      });
      onNavigate('dashboard');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to verify WhatsApp code';
      setClaimError(message);
      trackEvent('free_traffic_claim_verify_error', { page: location.pathname, message });
    } finally {
      setClaimVerificationSubmitting(false);
    }
  };

  const handleFreeTrafficClaim = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setClaimError('');
    setClaimSuccess('');

    if (claimProjectId) {
      onNavigate('dashboard');
      return;
    }

    if (claimVerificationLeadId) {
      await handleFreeTrafficVerify();
      return;
    }

    if (websiteCheckStatus === 'checking') {
      setClaimError('Wait for the website eligibility check to finish.');
      return;
    }

    if (websiteCheck && !websiteCheck.eligible) {
      setClaimError('Fix the failed website checks before claiming free traffic.');
      return;
    }

    setClaimSubmitting(true);

    try {
      const result = await db.submitFreeTrafficClaim({
        email: claimForm.email,
        whatsapp_number: claimForm.whatsappNumber,
        website_url: claimForm.websiteUrl,
        traffic_location: claimForm.trafficLocation,
        page_url: window.location.href,
        honeypot: claimForm.honeypot,
      });
      setClaimSuccess(result.message || 'Your free traffic request was received.');
      if (result.verification_required) {
        setClaimVerificationLeadId(result.lead_id || result.id);
        setClaimVerificationPhone(result.masked_whatsapp || '');
        setClaimDebugVerificationCode(result.debug_verification_code || '');
      } else {
        setClaimVerificationLeadId('');
        setClaimVerificationPhone('');
        setClaimDebugVerificationCode('');
        setClaimForm(prev => ({ ...prev, email: '', whatsappNumber: '', websiteUrl: '', honeypot: '' }));
      }
      trackEvent('free_traffic_claim_submit', { page: location.pathname, domain: result.normalized_domain, status: result.status });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to submit free traffic request';
      setClaimError(message);
      trackEvent('free_traffic_claim_error', { page: location.pathname, message });
    } finally {
      setClaimSubmitting(false);
    }
  };

  const claimWaitingForVerification = Boolean(claimVerificationLeadId) && !claimProjectId;
  const claimVerified = Boolean(claimProjectId);
  const claimActionDisabled = claimVerified
    ? false
    : claimWaitingForVerification
      ? claimVerificationSubmitting || claimVerificationCode.trim().length < 4
      : claimSubmitting || websiteCheckStatus === 'checking' || websiteCheckStatus === 'invalid';

  const handleAuthSuccess = (role: 'user' | 'admin') => {
    setAuthModalOpen(false);
    onLogin();
  };

  const toggleFaq = (index: number) => {
    const isOpening = openFaq !== index;
    setOpenFaq(isOpening ? index : null);
    if (isOpening) {
      trackEvent('faq_click', { faq_index: index, page: location.pathname });
    }
  };

  /* ─── Schema ─────────────────────────────────────────────────────────── */
  const entryPrice = PRICING_MATRIX.professional[VOLUME_STEPS[0]][1];
  const paidOffers = TIERS.flatMap((tier) => VOLUME_STEPS.map((volume) => ({
    "@type": "Offer",
    "name": `${tier.name} ${volume.toLocaleString('en-US')} Hit Pack`,
    "price": PRICING_MATRIX[tier.id][volume][1].toFixed(2),
    "priceCurrency": "USD",
    "url": "https://traffic-creator.com/signup",
    "description": `${volume.toLocaleString('en-US')} hits with ${tier.name.toLowerCase()} campaign controls.`
  })));

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Traffic Creator",
    "url": "https://traffic-creator.com/",
    "logo": "https://traffic-creator.com/favicon.svg",
    "description": "Browser-based website traffic campaign software for analytics validation, visibility research, and controlled experiments.",
    "brand": { "@type": "Brand", "name": "Traffic Creator" },
    "sameAs": [
      "https://twitter.com/trafficcreator"
    ]
  };

  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Traffic Creator — Website Traffic Generator",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "url": "https://traffic-creator.com/",
    "description": "A browser-based workspace for configuring, pacing, and reviewing GA4-visible website traffic campaigns.",
    "featureList": [
      "GA4-visible campaign delivery",
      "Country-level targeting on Professional",
      "State and city targeting on Expert",
      "Device and source controls",
      "Campaign pacing and scheduling",
      "Delivery monitoring"
    ]
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Configurable website traffic campaigns",
    "serviceType": "Website traffic campaign delivery and analytics testing",
    "url": "https://traffic-creator.com/",
    "provider": { "@type": "Organization", "name": "Traffic Creator" },
    "areaServed": "Worldwide",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Traffic Creator hit packs",
      "itemListElement": [
        {
          "@type": "Offer",
          "name": "6,000-session free test",
          "price": "0.00",
          "priceCurrency": "USD",
          "url": "https://traffic-creator.com/",
          "description": "A 24-hour test for one eligible public domain with GA4 or GTM installed, after email and WhatsApp verification."
        },
        ...paidOffers
      ]
    }
  };

  const faqs = [
    {
      question: "What is a website traffic generator and how does it work?",
      answer: "Traffic Creator runs configurable, browser-simulated traffic campaigns using residential routing on supported tiers. It is designed for analytics testing, visibility analysis, and campaign validation—not as proof of real customer demand."
    },
    {
      question: "How quickly does the traffic generator start delivering visitors?",
      answer: "Paid campaigns are designed for quick setup and launch. The free 6,000-session test begins after the domain check and WhatsApp verification have been completed."
    },
    {
      question: "Will generated traffic show in Google Analytics 4?",
      answer: "Campaign sessions and configured engagement events are designed to be visible in GA4. Your property, consent configuration, filters, and reporting setup can affect what you see, so every campaign should be verified in your own analytics."
    },
    {
      question: "Can this be used with ad-supported sites?",
      answer: "Review every advertising and monetization platform's policies before using paid traffic. Traffic Creator does not guarantee platform acceptance, ad treatment, or monetization outcomes."
    },
    {
      question: "Can I use the traffic generator to target specific countries or cities?",
      answer: "Traffic Creator offers broad global coverage. Available countries and targeting precision depend on the selected tier and supplier availability; Expert includes state and city targeting controls."
    },
    {
      question: "Is there a free website traffic generator option?",
      answer: "Yes. The free 24-hour test includes 6,000 sessions and does not require a credit card. One eligible public domain with GA4 or GTM installed, plus email and WhatsApp verification, is required before delivery starts."
    },
    {
      question: "Where can I buy website traffic that's safe and effective?",
      answer: `Paid packs start at ${formatPrice(entryPrice)} for ${VOLUME_STEPS[0].toLocaleString('en-US')} hits. Choose Professional for country-level controls or Expert for state and city targeting, then configure pacing and delivery settings in the dashboard.`
    },
    {
      question: "Do you offer a money-back guarantee?",
      answer: "If delivery falls short, the available remedy depends on the completed campaign and the current refund and delivery terms. Review those terms before purchasing or contact support for a specific order."
    },
    {
      question: "What makes Traffic Creator different from other SEO traffic generators?",
      answer: "Traffic Creator combines tier-based routing, geo and device controls, configurable behavior, pacing, scheduling, GA4-visible events, and delivery monitoring in one campaign workspace."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
    }))
  };

  const ssrPainted = typeof window !== 'undefined' && (window as any).__TC_SSR_PAINTED__;

  return (
    <div className={`min-h-screen bg-white text-gray-900 font-sans overflow-x-hidden ${ssrPainted ? 'tc-no-entrance' : ''}`} style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      <SEO
        title="Traffic Creator — Website Traffic Generator for SEO & Analytics (2026)"
        description={`Run configurable, GA4-visible website traffic campaigns with geo, device, pacing, and delivery controls. Paid packs start at ${formatPrice(entryPrice)} for ${VOLUME_STEPS[0].toLocaleString('en-US')} hits.`}
        keywords="website traffic generator, buy website traffic, web traffic generator, traffic generator for website, buy seo traffic, site traffic generator, free website traffic generator, buy web traffic, traffic bot, buy website hits"
        image="/og-landing-new.jpg"
        schema={[organizationSchema, softwareSchema, serviceSchema, faqSchema]}
      />
      <style dangerouslySetInnerHTML={{ __html: chartAnimationCSS }} />

      {/* ═══════════════ STICKY HEADER ═══════════════ */}
      <header className={`fixed w-full z-50 transition-all duration-500 ${scrolled ? 'bg-white/90 backdrop-blur-xl shadow-[0_1px_3px_rgba(0,0,0,0.08)] border-b border-gray-100' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 h-16 md:h-20 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <span className="text-xl md:text-2xl font-black text-[#ff4d00] tracking-tight">TRAFFIC</span>
            <span className="hidden sm:inline-block text-[10px] font-bold bg-gray-900 text-white px-2 py-1 rounded uppercase tracking-widest">Creator</span>
          </div>

          <nav className="hidden lg:flex items-center gap-8 text-[13px] font-semibold text-gray-500">
            <a href="#problem" className="hover:text-gray-900 transition-colors">The Problem</a>
            <a href="#features" className="hover:text-gray-900 transition-colors">Features</a>
            <a href="#pricing" className="hover:text-gray-900 transition-colors">Pricing</a>
            <Link to="/blog" className="hover:text-gray-900 transition-colors">Blog</Link>
          </nav>

          <div className="flex items-center gap-3">
            <Link to="/login" className="hidden sm:block text-[13px] font-semibold text-gray-600 hover:text-gray-900 transition-colors px-3 py-2">
              Log In
            </Link>
            <Link
              to="/signup"
              onClick={() => {
                trackEvent('cta_click', { cta_text: 'Start Free', cta_location: 'header', page: location.pathname });
              }}
              className="bg-[#ff4d00] text-white px-5 md:px-6 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-gray-900 transition-all rounded-xl shadow-lg shadow-orange-500/20 hover:shadow-orange-500/0 inline-flex items-center justify-center"
            >
              Start Free →
            </Link>
          </div>
        </div>
      </header>

      {/* ═══════════════ HERO — DARK SPLIT ═══════════════ */}
      <section className="relative flex items-center overflow-hidden bg-gray-950" style={{ minHeight: '100dvh' }}>
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute top-[-20%] right-[-10%] w-[700px] h-[700px] rounded-full bg-[#ff4d00]/10 blur-[120px] animate-pulse" />
          <div className="absolute bottom-[-20%] left-[-10%] w-[500px] h-[500px] rounded-full bg-blue-500/8 blur-[100px]" />
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        </div>

        <div className="max-w-7xl mx-auto px-6 pt-32 pb-20 relative z-10 grid lg:grid-cols-2 gap-16 items-center w-full">
          {/* Left: Copy */}
          <div className="space-y-8">
            <div className="tc-fade-up inline-flex items-center gap-2 bg-white/5 border border-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-xs font-bold text-gray-400 uppercase tracking-wider">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              Live Network — <span className="text-gray-300 tabular-nums">{liveCampaigns.toLocaleString()}</span> campaigns active
            </div>

            <h1 className="tc-fade-up text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] font-black tracking-tighter leading-[0.95] text-white" style={{ animationDelay: '0.08s' }} elementtiming="lcp">
              <span className="text-xs sm:text-sm font-bold tracking-widest text-[#ff4d00] uppercase mb-4 block">GA4-Verified Traffic · 60,000 Sessions from $19.95</span>
              Compete in{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ff4d00] via-orange-400 to-amber-400">
                Search.
              </span>
            </h1>

            <p className="tc-fade-up text-lg md:text-xl text-gray-400 max-w-lg leading-relaxed" style={{ animationDelay: '0.16s' }}>
              Targeted website traffic from <span className="text-white font-semibold">190+ countries</span> with residential visitors, organic search signals, and GA4-visible sessions you can track in real time. From $19.95 for 60,000 sessions.
            </p>

            <div className="tc-fade-up flex flex-col sm:flex-row gap-4" style={{ animationDelay: '0.24s' }}>
              <button
                onClick={focusFreeTrafficForm}
                className="group bg-[#ff4d00] text-white px-8 py-4 text-sm font-bold uppercase tracking-wider hover:bg-white hover:text-gray-900 transition-all rounded-xl shadow-[0_10px_40px_-10px_rgba(255,77,0,0.5)] flex items-center justify-center gap-3"
              >
                Get 6,000 Free Sessions <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                href="#how-it-works"
                className="group border border-white/15 text-white/70 px-8 py-4 text-sm font-bold uppercase tracking-wider hover:border-white/40 hover:text-white transition-all rounded-xl flex items-center justify-center gap-2 backdrop-blur-sm"
              >
                <Play size={16} className="fill-white/50" /> See How It Works
              </a>
            </div>

            <div className="tc-fade-up flex items-center gap-6 pt-4" style={{ animationDelay: '0.32s' }}>
              <div className="flex items-center rounded-full border border-white/10 bg-white/[0.04] px-2 py-1.5 shadow-[0_12px_30px_-18px_rgba(255,77,0,0.65)] backdrop-blur-sm">
                <div className="flex -space-x-3" aria-label="Traffic Creator customer profiles">
                  {[
                    { initials: 'AK', gradient: 'linear-gradient(135deg, #ff7a1a 0%, #dc2626 100%)' },
                    { initials: 'MR', gradient: 'linear-gradient(135deg, #60a5fa 0%, #4f46e5 100%)' },
                    { initials: 'TS', gradient: 'linear-gradient(135deg, #34d399 0%, #0f766e 100%)' },
                    { initials: 'LV', gradient: 'linear-gradient(135deg, #f472b6 0%, #be185d 100%)' },
                    { initials: 'JP', gradient: 'linear-gradient(135deg, #facc15 0%, #f97316 100%)' },
                  ].map((profile, i) => (
                    <div
                      key={profile.initials}
                      className="relative flex h-9 w-9 items-center justify-center rounded-full border-2 border-gray-950 text-[10px] font-black text-white shadow-lg ring-1 ring-white/15"
                      style={{ background: profile.gradient, zIndex: 10 - i }}
                    >
                      <span>{profile.initials}</span>
                      <span className="absolute -right-0.5 bottom-0 h-2.5 w-2.5 rounded-full border-2 border-gray-950 bg-emerald-400" />
                    </div>
                  ))}
                  <div className="relative flex h-9 w-9 items-center justify-center rounded-full border-2 border-gray-950 bg-gray-800 text-[10px] font-black text-white shadow-lg ring-1 ring-white/15">
                    +24
                  </div>
                </div>
              </div>
              <div className="text-xs text-gray-500">
                <span className="text-white font-bold">5,000+</span> marketers trust us
              </div>
              <div className="flex items-center gap-1 text-[#ff4d00]">
                <BadgeCheck size={14} />
                <span className="text-xs font-bold uppercase tracking-wide">Quality controls</span>
              </div>
            </div>
          </div>

          {/* Right: Free Traffic Sign-In */}
          <div className="tc-fade-up relative" style={{ animationDelay: '0.2s' }}>
            <form
              ref={claimFormRef}
              onSubmit={handleFreeTrafficClaim}
              className="relative bg-gray-900 rounded-2xl border border-white/10 backdrop-blur-sm p-5 sm:p-6 shadow-2xl"
            >
              <input
                type="text"
                name="company"
                value={claimForm.honeypot}
                onChange={(event) => handleClaimInputChange('honeypot', event.target.value)}
                className="hidden"
                tabIndex={-1}
                autoComplete="off"
              />

              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full bg-emerald-400/10 border border-emerald-400/20 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-emerald-300 mb-3">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                    Free GA4 test run
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white">
                    Get 6,000 trackable sessions after a 60-second check
                  </h2>
                  <p className="mt-2 text-sm leading-6 text-gray-400">
                    We check your domain, confirm your WhatsApp number, create your account, and launch one free 24h test campaign.
                  </p>
                  <div className="mt-4 grid grid-cols-3 gap-2 text-[10px] font-bold uppercase tracking-wider text-gray-400">
                    {['Check domain', 'Verify WhatsApp', 'Traffic starts'].map((step, index) => (
                      <div key={step} className="flex min-h-10 items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-2.5 py-2">
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#ff4d00] text-[10px] text-white">
                          {index + 1}
                        </span>
                        <span className="leading-tight">{step}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="hidden sm:flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#ff4d00]/15 text-[#ff7b3d]">
                  <TrendingUp size={22} />
                </div>
              </div>

              <div className="grid gap-4">
                <label className="block">
                  <span className="mb-1.5 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-400">
                    <Mail size={14} /> Email
                  </span>
                  <input
                    name="email"
                    type="email"
                    required
                    disabled={claimWaitingForVerification || claimVerified || claimSubmitting}
                    value={claimForm.email}
                    onChange={(event) => handleClaimInputChange('email', event.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    className="w-full rounded-xl border border-white/10 bg-gray-950/80 px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-gray-600 focus:border-[#ff4d00] focus:ring-2 focus:ring-[#ff4d00]/20 disabled:cursor-not-allowed disabled:opacity-70"
                  />
                </label>

                <label className="block">
                  <span className="mb-1.5 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-400">
                    <Phone size={14} /> WhatsApp <span className="text-[#ff7b3d]">Required</span>
                  </span>
                  <input
                    type="tel"
                    required
                    aria-required="true"
                    disabled={claimWaitingForVerification || claimVerified || claimSubmitting}
                    value={claimForm.whatsappNumber}
                    onChange={(event) => handleClaimInputChange('whatsappNumber', event.target.value)}
                    placeholder="+1 555 123 4567"
                    autoComplete="tel"
                    className="w-full rounded-xl border border-white/10 bg-gray-950/80 px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-gray-600 focus:border-[#ff4d00] focus:ring-2 focus:ring-[#ff4d00]/20 disabled:cursor-not-allowed disabled:opacity-70"
                  />
                  <span className="mt-1.5 block text-[11px] font-medium text-gray-500">
                    Code only. No spam.
                  </span>
                </label>

                <label className="block">
                  <span className="mb-1.5 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-400">
                    <Link2 size={14} /> Website domain
                  </span>
                  <input
                    type="text"
                    required
                    disabled={claimWaitingForVerification || claimVerified || claimSubmitting}
                    value={claimForm.websiteUrl}
                    onChange={(event) => handleClaimInputChange('websiteUrl', event.target.value)}
                    placeholder="example.com"
                    autoComplete="url"
                    className="w-full rounded-xl border border-white/10 bg-gray-950/80 px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-gray-600 focus:border-[#ff4d00] focus:ring-2 focus:ring-[#ff4d00]/20 disabled:cursor-not-allowed disabled:opacity-70"
                  />
                  {websiteCheckStatus !== 'idle' && (
                    <div className={`mt-2 rounded-xl border px-3 py-2.5 text-xs ${
                      websiteCheckStatus === 'valid'
                        ? 'border-emerald-400/20 bg-emerald-400/10 text-emerald-100'
                        : websiteCheckStatus === 'checking'
                          ? 'border-white/10 bg-white/[0.04] text-gray-300'
                          : 'border-rose-400/20 bg-rose-400/10 text-rose-100'
                    }`}>
                      <div className="flex items-center gap-2 font-bold">
                        {websiteCheckStatus === 'checking' ? (
                          <Loader2 size={14} className="shrink-0 animate-spin" />
                        ) : websiteCheckStatus === 'valid' ? (
                          <Check size={14} className="shrink-0" />
                        ) : (
                          <AlertCircle size={14} className="shrink-0" />
                        )}
                        <span>{websiteCheckMessage}</span>
                      </div>

                      {websiteCheck && (
                        <div className="mt-2 space-y-1.5 text-gray-300">
                          {websiteCheck.checks.map((check) => (
                            <div key={check.key} className="flex items-start gap-2">
                              {check.passed ? (
                                <Check size={13} className="mt-0.5 shrink-0 text-emerald-300" />
                              ) : (
                                <AlertCircle size={13} className="mt-0.5 shrink-0 text-rose-300" />
                              )}
                              <span>
                                <span className="font-semibold text-white">{check.label}:</span>{' '}
                                <span className="text-gray-400">{check.message}</span>
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </label>

                <div className="relative" ref={countryDropdownRef}>
                  <span className="mb-1.5 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-400">
                    <MapPin size={14} /> Visitor location
                  </span>
                  <div className="relative">
                    <Search size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                    <input
                      type="text"
                      required
                      role="combobox"
                      aria-expanded={countryDropdownOpen}
                      aria-controls="traffic-location-options"
                      disabled={claimWaitingForVerification || claimVerified || claimSubmitting}
                      value={countrySearch}
                      onFocus={(event) => {
                        setCountryDropdownOpen(true);
                        event.currentTarget.select();
                      }}
                      onChange={(event) => {
                        const value = event.target.value;
                        setCountrySearch(value);
                        handleClaimInputChange('trafficLocation', value);
                        setCountryDropdownOpen(true);
                      }}
                      placeholder="Search country"
                      className="w-full rounded-xl border border-white/10 bg-gray-950/80 px-10 py-3 text-sm text-white outline-none transition-colors placeholder:text-gray-600 focus:border-[#ff4d00] focus:ring-2 focus:ring-[#ff4d00]/20 disabled:cursor-not-allowed disabled:opacity-70"
                    />
                    <ChevronDown size={16} className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-500" />
                  </div>

                  {countryDropdownOpen && (
                    <div
                      id="traffic-location-options"
                      className="absolute left-0 right-0 top-full z-20 mt-2 max-h-56 overflow-y-auto rounded-xl border border-white/10 bg-gray-950 p-1.5 shadow-2xl"
                    >
                      {filteredTrafficLocations.length > 0 ? (
                        filteredTrafficLocations.map(({ code, name }) => (
                          <button
                            key={code}
                            type="button"
                            onClick={() => handleCountrySelect(name)}
                            className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm transition-colors ${claimForm.trafficLocation === name ? 'bg-[#ff4d00] text-white' : 'text-gray-300 hover:bg-white/10 hover:text-white'}`}
                          >
                            <span>{name}</span>
                            <span className="text-[10px] font-bold uppercase tracking-wider opacity-60">{code}</span>
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-3 text-sm text-gray-500">No country found</div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {claimError && (
                <div className="mt-4 flex items-start gap-2 rounded-xl border border-rose-400/20 bg-rose-400/10 px-4 py-3 text-sm text-rose-200">
                  <AlertCircle size={16} className="mt-0.5 shrink-0" />
                  <span>{claimError}</span>
                </div>
              )}

              {claimSuccess && (
                <div className="mt-4 flex items-start gap-2 rounded-xl border border-emerald-400/20 bg-emerald-400/10 px-4 py-3 text-sm text-emerald-200">
                  <Check size={16} className="mt-0.5 shrink-0" />
                  <span>{claimSuccess}</span>
                </div>
              )}

              {claimWaitingForVerification && (
                <div className="mt-4 rounded-xl border border-[#ff4d00]/25 bg-[#ff4d00]/10 p-4">
                  <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#ffb18a]">
                    <Shield size={14} /> WhatsApp verification
                  </div>
                  <p className="mb-3 text-sm leading-5 text-gray-300">
                    Enter the code sent to {claimVerificationPhone || 'your WhatsApp number'} to create the account and start the traffic run.
                  </p>
                  <input
                    name="verificationCode"
                    type="text"
                    required
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={claimVerificationCode}
                    onChange={(event) => {
                      setClaimVerificationCode(event.target.value.replace(/\s+/g, '').slice(0, 12));
                      if (claimError) setClaimError('');
                    }}
                    placeholder="6-digit code"
                    className="w-full rounded-xl border border-white/10 bg-gray-950/80 px-4 py-3 text-sm font-bold tracking-widest text-white outline-none transition-colors placeholder:font-medium placeholder:tracking-normal placeholder:text-gray-600 focus:border-[#ff4d00] focus:ring-2 focus:ring-[#ff4d00]/20"
                  />
                  {claimDebugVerificationCode && (
                    <p className="mt-2 text-xs font-bold text-[#ffb18a]">
                      Local test code: <span className="font-mono">{claimDebugVerificationCode}</span>
                    </p>
                  )}
                </div>
              )}

              {claimVerified && claimTemporaryPassword && (
                <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.04] p-4 text-sm text-gray-300">
                  <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-400">
                    <Lock size={14} /> Account created
                  </div>
                  <p>
                    Temporary password: <span className="font-mono font-bold text-white">{claimTemporaryPassword}</span>
                  </p>
                </div>
              )}

              <button
                type={claimVerified ? 'button' : 'submit'}
                onClick={claimVerified ? () => onNavigate('dashboard') : undefined}
                disabled={claimActionDisabled}
                className="mt-5 flex w-full items-center justify-center gap-3 rounded-xl bg-[#ff4d00] px-6 py-4 text-sm font-bold uppercase tracking-wider text-white shadow-[0_10px_40px_-10px_rgba(255,77,0,0.5)] transition-all hover:bg-white hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {claimVerified ? (
                  <>
                    Open dashboard <ArrowRight size={18} />
                  </>
                ) : claimVerificationSubmitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Verifying code
                  </>
                ) : claimWaitingForVerification ? (
                  <>
                    Start my free 24h test <ArrowRight size={18} />
                  </>
                ) : claimSubmitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Sending request
                  </>
                ) : (
                  <>
                    Start my free 24h test <ArrowRight size={18} />
                  </>
                )}
              </button>
              <div className="mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] font-bold text-gray-500">
                <span>No credit card</span>
                <span className="text-[#ff4d00]">·</span>
                <span>One verified domain</span>
                <span className="text-[#ff4d00]">·</span>
                <span>Starts after WhatsApp verification</span>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                {[
                  ['6,000', 'sessions'],
                  ['GA4/GTM', 'checked'],
                  ['24h', 'delivery'],
                ].map(([value, label]) => (
                  <div key={label} className="rounded-xl border border-white/10 bg-white/[0.03] px-2 py-3">
                    <div className="text-base font-black text-white">{value}</div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{label}</div>
                  </div>
                ))}
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* ═══════════════ SOCIAL PROOF METRICS BAR ═══════════════ */}
      <section ref={statsRef} className="bg-white border-b border-gray-100 py-10 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-8">
            {[
              { value: <AnimatedCounter end={14847} trigger={statsInView} suffix="+" />, label: 'Active Users', icon: <Users size={18} /> },
              { value: <AnimatedCounter end={2} trigger={statsInView} suffix="B+" />, label: 'Visitors Delivered', icon: <Eye size={18} /> },
              { value: <AnimatedCounter end={193} trigger={statsInView} suffix="+" />, label: 'Countries', icon: <Globe size={18} /> },
              { value: 'QA', label: 'Quality Controls', icon: <BadgeCheck size={18} /> },
              { value: '99.9%', label: 'Uptime SLA', icon: <Server size={18} /> },
            ].map((stat, i) => (
              <div key={i} className="flex items-center gap-3 group">
                <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center text-[#ff4d00] group-hover:bg-[#ff4d00] group-hover:text-white transition-colors shrink-0">
                  {stat.icon}
                </div>
                <div>
                  <div className="text-xl md:text-2xl font-black text-gray-900">{stat.value}</div>
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <LazySection minHeight="600px" animate={false}>
        <Suspense fallback={<LazyLandingFallback minHeight="600px" />}>
          <LazyProblem />
        </Suspense>
      </LazySection>

      <LazySection minHeight="600px" animate={false}>
        <Suspense fallback={<LazyLandingFallback minHeight="600px" />}>
          <LazyHowItWorks />
        </Suspense>
      </LazySection>

      <LazySection minHeight="600px" animate={false}>
        <Suspense fallback={<LazyLandingFallback minHeight="600px" />}>
          <LazyFeatures onOpenAuth={() => handleOpenAuth('signup')} currentPath={location.pathname} />
        </Suspense>
      </LazySection>

      <LazySection minHeight="600px" animate={false}>
        <Suspense fallback={<LazyLandingFallback minHeight="600px" />}>
          <LazyPricing onOpenAuth={() => handleOpenAuth('signup')} currentPath={location.pathname} />
        </Suspense>
      </LazySection>

      <LazySection minHeight="400px" animate={false}>
        <Suspense fallback={<LazyLandingFallback />}>
          <LazyTestimonials />
        </Suspense>
      </LazySection>

      <LazySection minHeight="400px" animate={false}>
        <Suspense fallback={<LazyLandingFallback />}>
          <LazyComparison onOpenAuth={() => handleOpenAuth('signup')} currentPath={location.pathname} />
        </Suspense>
      </LazySection>

      <LazySection minHeight="400px" animate={false}>
        <Suspense fallback={<LazyLandingFallback />}>
          <LazyGuarantee />
        </Suspense>
      </LazySection>

      <LazySection minHeight="600px" animate={false}>
        <Suspense fallback={<LazyLandingFallback minHeight="600px" />}>
          <LazyFaq currentPath={location.pathname} faqs={faqs} />
        </Suspense>
      </LazySection>

      {/* ═══════════════ FINAL CTA ═══════════════ */}
      <section ref={ctaRef} className="relative py-24 md:py-32 px-6 bg-gray-950 overflow-hidden">
        <div className="absolute inset-0 bg-[#050914]" aria-hidden="true">
          {ctaInView && (
            <>
              <div
                className="tc-cta-grid absolute inset-0 opacity-[0.08]"
                style={{
                  backgroundImage: 'linear-gradient(rgba(255,77,0,0.45) 1px, transparent 1px), linear-gradient(90deg, rgba(255,77,0,0.45) 1px, transparent 1px)',
                  backgroundSize: '48px 48px'
                }}
              />
              <div className="tc-cta-beam absolute -top-24 bottom-[-6rem] left-0 w-1/3 bg-gradient-to-r from-transparent via-[#ff4d00]/28 to-transparent blur-2xl" />
              <div className="tc-cta-line absolute left-0 right-0 top-16 h-px bg-gradient-to-r from-transparent via-[#ff4d00]/70 to-transparent" />
              <div className="tc-cta-line absolute left-0 right-0 bottom-20 h-px bg-gradient-to-r from-transparent via-amber-400/45 to-transparent" style={{ animationDelay: '1.8s' }} />
            </>
          )}
          <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(255,77,0,0.16)_0%,transparent_34%,rgba(255,77,0,0.08)_64%,transparent_100%)]" />
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-6xl font-black text-white tracking-tight mb-6">
            Start Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ff4d00] to-amber-400">Traffic Generator</span> Today
          </h2>
          <p className="text-gray-400 text-lg mb-10 max-w-xl mx-auto">
            Join marketers who buy website traffic for analytics validation, campaign testing, and SEO research. No contracts. Instant delivery. 72-hour delivery guarantee.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => {
                trackEvent('cta_click', { cta_text: 'Get 6,000 Free Sessions', cta_location: 'bottom_cta', page: location.pathname });
                handleOpenAuth('signup');
              }}
              className="bg-[#ff4d00] text-white px-10 py-5 rounded-xl text-sm font-bold uppercase tracking-wider hover:bg-white hover:text-gray-900 transition-all shadow-[0_10px_40px_-10px_rgba(255,77,0,0.5)] flex items-center justify-center gap-2"
            >
              Get 6,000 Free Sessions <ArrowRight size={18} />
            </button>
            <Link to="/blog" className="border border-white/15 text-white/70 px-10 py-5 rounded-xl text-sm font-bold uppercase tracking-wider hover:border-white/40 hover:text-white transition-all flex items-center justify-center backdrop-blur-sm">
              Read Our Blog
            </Link>
          </div>
          <div className="mt-8 flex items-center justify-center gap-6 text-gray-500 text-xs font-medium">
            <span className="flex items-center gap-1.5"><Check size={14} className="text-[#ff4d00]" /> No credit card</span>
            <span className="flex items-center gap-1.5"><Check size={14} className="text-[#ff4d00]" /> 6,000 free sessions</span>
            <span className="flex items-center gap-1.5"><Check size={14} className="text-[#ff4d00]" /> Cancel anytime</span>
          </div>
        </div>
      </section>

      {/* ═══════════════ FOOTER ═══════════════ */}
      <footer className="bg-white border-t border-gray-100 py-12 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <span className="text-lg font-black text-[#ff4d00]">TRAFFIC</span>
              <span className="text-[10px] font-bold bg-gray-900 text-white px-2 py-0.5 rounded uppercase tracking-widest">Creator</span>
            </div>
            <div className="text-xs text-gray-400">© 2026 EasyTrafficBot UG (haftungsbeschränkt)<br/>Amtsgericht Wuppertal, HRB 30863</div>
            <div className="text-xs text-gray-400 mt-1">More from EasyTrafficBot UG: <a href="https://gathmo.com" target="_blank" rel="noopener" className="text-gray-500 underline hover:text-[#ff4d00] transition-colors">Gathmo</a> — QR photo sharing for weddings & events</div>
          </div>
          <div className="flex flex-wrap md:justify-end gap-x-6 gap-y-3 text-sm text-gray-500 font-medium max-w-3xl">
            <Link to="/about" className="hover:text-[#ff4d00] transition-colors">About Us</Link>
            <Link to="/blog" className="hover:text-[#ff4d00] transition-colors">Blog</Link>
            <Link to="/helpdesk" className="hover:text-[#ff4d00] transition-colors">Help Center</Link>
            <span className="w-full h-px bg-gray-100 my-1 hidden md:block"></span>

            <Link to="/website-traffic-generator" className="hover:text-[#ff4d00] transition-colors">Website Traffic Generator</Link>
            <Link to="/high-quality-website-traffic" className="hover:text-[#ff4d00] transition-colors">Buy Quality Traffic</Link>
            <Link to="/buy-organic-traffic" className="hover:text-[#ff4d00] transition-colors">Buy Organic Traffic</Link>
            <Link to="/buy-seo-traffic" className="hover:text-[#ff4d00] transition-colors">Buy SEO Traffic</Link>
            <Link to="/buy-website-hits" className="hover:text-[#ff4d00] transition-colors">Website Hits</Link>
            <Link to="/ctr-manipulation-tool" className="hover:text-[#ff4d00] transition-colors">CTR Analysis</Link>
            <Link to="/white-label-traffic-api" className="hover:text-[#ff4d00] transition-colors">Traffic API</Link>
            <Link to="/alternatives/babylontraffic" className="hover:text-[#ff4d00] transition-colors">BabylonTraffic Alternatives</Link>
            
            <span className="w-full h-px bg-gray-100 my-1 hidden md:block"></span>
            
            <Link to="/legal/refund" className="hover:text-[#ff4d00] transition-colors">Refund Policy</Link>
            <Link to="/legal/privacy" className="hover:text-[#ff4d00] transition-colors">Privacy</Link>
            <Link to="/legal/terms" className="hover:text-[#ff4d00] transition-colors">Terms Of Service</Link>
            <Link to="/legal/agb" className="hover:text-[#ff4d00] transition-colors">AGB</Link>
            <Link to="/legal/impressum" className="hover:text-[#ff4d00] transition-colors">Impressum</Link>
          </div>
        </div>
      </footer>
      {/* ═══════════════ AUTH MODAL ═══════════════ */}
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        initialMode={authMode}
        onSuccess={handleAuthSuccess}
        message={authMessage}
      />

      {/* ═══════════════ MOBILE STICKY CTA ═══════════════ */}
      <div className={`fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-gray-100 p-3 flex items-center justify-between gap-3 md:hidden transition-transform duration-300 ${scrolled ? 'translate-y-0' : 'translate-y-full'}`}>
        <div className="text-xs">
          <div className="font-black text-gray-900">🔥 6,000 Free Sessions</div>
          <div className="text-gray-400">No credit card · Instant delivery</div>
        </div>
        <button
          onClick={() => handleOpenAuth('signup')}
          className="bg-[#ff4d00] text-white px-6 py-3 rounded-xl text-xs font-bold uppercase tracking-wider shrink-0 shadow-lg shadow-orange-500/20 animate-pulse" style={{ animationDuration: '2s' }}
        >
          Claim Now →
        </button>
      </div>
    </div>
  );
};

export default OptimizedLandingPage;
